import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
  } from "react";
  import { useDispatch, useSelector } from "react-redux";
  import { get, isEmpty } from "lodash";

  import { fetchUserDetails } from "../actions/userprofile";
  import { loggedInUser, getUserId } from "../helpers/common";
  import { convertToNumber } from "../helpers/validations";
  import { USER_TYPE_AGENT, USER_TYPE_MEMBER } from "../constants/common";

  const PERIOD = {
    FREE: 1,
    MONTHLY: 2,
    ANNUAL: 3,
  };

  const PLAN_TYPE = {
    TRIAL: "Trial",
    LITE: "Lite",
    MEMBER: "Member",
    PRO_MEMBER: "Pro Member",
    INVESTOR_MEMBER: "Investor Member",
    PRO_PREFERRED: "Pro Preferred",
  };

  const UserContext = createContext(null);

  /**
   * Provider that centralizes user details + premium flags + UI flags.
   * Initializes synchronously to avoid UI blink (e.g. shouldToggleColor) and
   * then hydrates with async Redux data.
   */
  export const UserProvider = ({ children }) => {
    const dispatch = useDispatch();

    // ---------- Sync init to prevent "blink" ----------
    const computeSyncState = () => {
      const user = loggedInUser();
      const userInfo = get(user, "userInfo", null);
      const _usertype = parseInt(get(userInfo, "userType", null));
      const premium = convertToNumber(_usertype) === USER_TYPE_AGENT;

      let subdomain = "";
      if (typeof window !== "undefined") {
        const hostname = window.location.hostname || "";
        subdomain = hostname.split(".")[0]?.toLowerCase?.() || "";
      }

      const shouldToggleColor =
        (premium || subdomain === "cove" || subdomain === "blc" || subdomain === "momentum") &&
        subdomain !== "stellar" &&
        subdomain !== "test-mls" &&
        subdomain !== "bls" &&
        subdomain !== "lar";

      return { premium, shouldToggleColor };
    };

    const [{ premium: premiumInitial, shouldToggleColor }, setSyncState] =
      useState(() => computeSyncState());

    // If hostname can change during session (e.g. micro-frontend, SSR rehydrate),
    // update the sync state once on mount.
    useEffect(() => {
      setSyncState(computeSyncState());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------- Async store-driven data ----------
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const userDetailsFromStore = useSelector(
      (state) => state.userProfile.userDetails,
    );
    const userDetails = get(userDetailsFromStore, 0);

    const userId = getUserId();

    const loadUserData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        await dispatch(fetchUserDetails()).unwrap();
      } catch (err) {
        setError(err?.message || "Failed to fetch user details");
      } finally {
        setLoading(false);
      }
    }, [dispatch]);

    useEffect(() => {
      if (userId) {
        loadUserData();
      } else {
        setLoading(false);
      }
    }, [userId, loadUserData]);

    // ---------- Derived values ----------
    const userType = get(userDetails, "UserTypeId", null);

    // Keep initial premium flag to avoid flicker until store resolves
    const isAgent = useMemo(() => {
      return userType === USER_TYPE_AGENT || premiumInitial;
    }, [userType, premiumInitial]);

    const isMember = useMemo(() => {
      return userType === USER_TYPE_MEMBER;
    }, [userType]);

    const getSubscriptionPeriod = useCallback(() => {
      return get(userDetails, "period", null);
    }, [userDetails]);

    const isFreePlan = useMemo(() => {
      const period = getSubscriptionPeriod();
      if (!period) return true;
      return period === PERIOD.FREE;
    }, [getSubscriptionPeriod]);

    const isPremiumUser = useMemo(() => {
      if (isEmpty(userDetailsFromStore)) return isAgent && !isFreePlan; // best-effort before hydration
      return !isFreePlan && isAgent;
    }, [userDetailsFromStore, isFreePlan, isAgent]);

    const getCurrentPlan = useCallback(() => {
      if (isEmpty(userDetailsFromStore)) return "";
      const firstUserPlan = get(userDetailsFromStore, [0, "PlanName"]);
      const secondUserPlan = get(userDetailsFromStore, [1, "PlanName"]);

      if (userDetailsFromStore.length > 1) {
        return firstUserPlan === PLAN_TYPE.TRIAL ? secondUserPlan : firstUserPlan;
      }

      return firstUserPlan || "";
    }, [userDetailsFromStore]);

    const isTrialPeriod = useMemo(
      () => getCurrentPlan() === PLAN_TYPE.TRIAL,
      [getCurrentPlan],
    );

    const contextValue = useMemo(
      () => ({
        // raw / store
        userDetails,
        // status
        loading,
        error,
        // roles & plans
        isPremiumUser,
        isTrialPeriod,
        isFreePlan,
        isAgent,
        getCurrentPlan,
        getSubscriptionPeriod,
        loadUserData,
        isUserLoggedIn: !!userId,
        // ui flags
        shouldToggleColor,
      }),
      [
        userDetails,
        loading,
        error,
        isPremiumUser,
        isTrialPeriod,
        isFreePlan,
        isAgent,
        isMember,
        getCurrentPlan,
        getSubscriptionPeriod,
        loadUserData,
        userId,
        shouldToggleColor,
      ],
    );

    return (
      <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
    );
  };

  export const useUserDetails = () => {
    const ctx = useContext(UserContext);
    if (!ctx)
      throw new Error("useUserDetails must be used within a UserProvider");
    return ctx;
  };

  export default useUserDetails;
